"use client";

import React from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Info } from "lucide-react";
const gearItems = [{
  id: "gear-1",
  name: "Leica M6",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1556059/img/leica_m6_top.png",
  specs: ["35mm Rangefinder", "0.72x Viewfinder", "Mechanical Shutter"],
  history: "Introduced in 1984, the Leica M6 combines the classic M3 silhouette with a modern through-the-lens light meter. It remains the benchmark for rangefinder photography."
}, {
  id: "gear-2",
  name: "Hasselblad 500C/M Finder",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1556059/img/hasselblad_finder.png",
  specs: ["Waist-Level Finder", "Pop-up Magnifier", "Modular System"],
  history: "The iconic waist-level finder allows for a unique top-down composition style, encouraging a slower, more deliberate approach to framing medium format images."
}, {
  id: "gear-3",
  name: "Kodak Tri-X 400",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1556059/img/trix_400_roll.png",
  specs: ["B&W Negative Film", "ISO 400", "Classic Grain Structure"],
  history: "A photojournalist staple since 1954. Known for its forgiving exposure latitude and distinctive gritty aesthetic that defines the look of 20th-century documentary photography."
}, {
  id: "gear-4",
  name: "Sekonic L-398A",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1556059/img/sekonic_meter.png",
  specs: ["Analog Light Meter", "Amorphous Silicon", "Incident & Reflective"],
  history: "The Studio Deluxe III is a battery-free analog meter. Its needle display provides a visual representation of light intensity, essential for precise exposure control."
}];
export default function TheAnalogToolkit() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="the-analog-toolkit" ref={ref} className="dark bg-background text-foreground relative py-24 overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none opacity-10 bg-[linear-gradient(to_right,#808080_1px,transparent_1px),linear-gradient(to_bottom,#808080_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_at_center,black_60%,transparent_100%)]" />

      <div className="container relative z-10 mx-auto px-6">
        <div className={`transition-all duration-1000 ease-out transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="mb-16 text-center max-w-2xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-serif tracking-tight mb-4 text-foreground">
              The Analog Toolkit
            </h2>
            <p className="text-muted-foreground text-lg">
              Precision instruments for the deliberate image-maker. Hover over each piece to uncover its legacy.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {gearItems.map((item, i) => <div key={item.id} data-index={i} className="group">
                <TooltipProvider delayDuration={100}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Card className="bg-card/50 hover:bg-card/80 border-border/50 backdrop-blur-sm transition-colors duration-300 cursor-help h-full overflow-hidden">
                        <CardContent className="p-6 flex flex-col items-center h-full">
                          {}
                          <div className="relative w-full aspect-square mb-8 flex items-center justify-center p-4 bg-secondary/20 rounded-full group-hover:bg-secondary/30 transition-colors duration-500">
                            <img src={item.image} alt={item.name} className="w-full h-full object-contain drop-shadow-2xl transition-transform duration-500 group-hover:scale-110 group-hover:-rotate-3" />
                            <div className="absolute bottom-2 right-2 md:hidden text-muted-foreground/50">
                                <Info className="w-4 h-4" />
                            </div>
                          </div>

                          {}
                          <div className="text-center mt-auto w-full">
                            <h3 className="text-lg font-medium text-foreground mb-3 border-b border-border/50 pb-2">
                              {item.name}
                            </h3>
                            <ul className="space-y-1.5">
                              {item.specs.map((spec, idx) => <li key={idx} className="text-xs font-mono text-muted-foreground uppercase tracking-wider">
                                  {spec}
                                </li>)}
                            </ul>
                          </div>
                        </CardContent>
                      </Card>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs bg-popover text-popover-foreground border-border p-4 shadow-xl">
                      <p className="text-sm leading-relaxed font-sans">
                        {item.history}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}